import { useMemo, useState } from 'react';
import { Control, useController, useFieldArray } from 'react-hook-form';
import { Country, getCountryCallingCode } from 'react-phone-number-input';

import {
  BrandDetailsFormValues,
  SenderIdConfigurationFormValues,
  TrademarkVerificationFormValues,
} from './types';
import { ValidationRule } from '../../types';
import {
  createEinValidator,
  createMaxLengthValidator,
  createPatternValidator,
  createRequiredPhoneValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import regexExpressions from '@/constants/regexExpressions';
import { EntityTypes } from '@/shared_elements/enums';

export const useBrandDetailsControllers = (
  control: Control<BrandDetailsFormValues, any, BrandDetailsFormValues>,
  validationRules: ValidationRule[]
) => {
  const [phoneCountry, setPhoneCountry] = useState<Country>();
  const phoneCountryCode = useMemo(() => {
    try {
      return phoneCountry ? getCountryCallingCode(phoneCountry) : '';
    } catch (e) {
      return '';
    }
  }, [phoneCountry]);
  const einValidationRule = useMemo(
    () => validationRules.find((rule) => rule.attributeName === 'ein'),
    [validationRules]
  );

  // We hoist entityType, country, and einIssuingCountry since other controllers depend on their values
  const {
    field: { value: entityType, onChange: setEntityType },
    fieldState: { error: entityTypeError },
  } = useController({
    control,
    name: 'entityType',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Entity Type'),
      },
    },
  });

  const {
    field: { value: country, onChange: setCountry },
    fieldState: { error: countryError },
  } = useController({
    control,
    name: 'country',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select Country of Registration'
        ),
      },
    },
  });

  const {
    field: {
      disabled: companyNameDisabled,
      value: companyName,
      onChange: setCompanyName,
      onBlur: validateCompanyName,
    },
    fieldState: { error: companyNameError },
  } = useController({
    control,
    name: 'companyName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Brand Legal Name'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: {
      value: displayName,
      onChange: setDisplayName,
      onBlur: validateDisplayName,
    },
    fieldState: { error: displayNameError },
  } = useController({
    control,
    name: 'displayName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Brand DBA Name'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: {
      disabled: verticalDisabled,
      value: vertical,
      onChange: setVertical,
    },
    fieldState: { error: verticalError },
  } = useController({
    control,
    name: 'vertical',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Vertical Type'),
      },
    },
  });

  const {
    field: {
      disabled: stockSymbolDisabled,
      value: stockSymbol,
      onChange: setStockSymbol,
      onBlur: validateStockSymbol,
    },
    fieldState: { error: stockSymbolError },
  } = useController({
    control,
    name: 'stockSymbol',
    rules: {
      validate: {
        withValidMaxLength: createMaxLengthValidator(10),
      },
    },
  });

  const {
    field: {
      disabled: stockExchangeDisabled,
      value: stockExchange,
      onChange: setStockExchange,
    },
    fieldState: { error: stockExchangeError },
  } = useController({
    control,
    name: 'stockExchange',
  });

  const {
    field: {
      disabled: websiteDisabled,
      value: website,
      onChange: setWebsite,
      onBlur: validateWebsite,
    },
    fieldState: { error: websiteError },
  } = useController({
    control,
    name: 'website',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Website / Online Presence'
        ),
        withValidMaxLength: createMaxLengthValidator(255),
        withValidPattern: createPatternValidator(
          regexExpressions.url,
          'Invalid Website'
        ),
      },
    },
  });

  const {
    field: {
      disabled: firstNameDisabled,
      value: firstName,
      onChange: setFirstName,
      onBlur: validateFirstName,
    },
    fieldState: { error: firstNameError },
  } = useController({
    control,
    name: 'firstName',
    disabled: entityType !== EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter First Name'),
        withValidMaxLength: createMaxLengthValidator(100),
        withValidPattern: createPatternValidator(
          regexExpressions.name,
          'Invalid First Name'
        ),
      },
    },
  });

  const {
    field: {
      disabled: lastNameDisabled,
      value: lastName,
      onChange: setLastName,
      onBlur: validateLastName,
    },
    fieldState: { error: lastNameError },
  } = useController({
    control,
    name: 'lastName',
    disabled: entityType !== EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Last Name'),
        withValidMaxLength: createMaxLengthValidator(100),
        withValidPattern: createPatternValidator(
          regexExpressions.name,
          'Invalid Last Name'
        ),
      },
    },
  });

  const {
    field: { value: phone, onChange: setPhone, onBlur: validatePhone },
    fieldState: { error: phoneError },
  } = useController({
    control,
    name: 'phone',
    rules: {
      validate: {
        isRequired: createRequiredPhoneValidator(
          phoneCountryCode,
          'Please enter Support Phone Number'
        ),
        ...(phoneCountry === 'IN'
          ? { withValidMaxLength: createMaxLengthValidator(13) }
          : {}),
      },
    },
  });

  const {
    field: { value: email, onChange: setEmail, onBlur: validateEmail },
    fieldState: { error: emailError },
  } = useController({
    control,
    name: 'email',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Support Email Address'
        ),
        isValidMaxLength: createMaxLengthValidator(100),
        isValidEmail: createPatternValidator(
          regexExpressions.email,
          'Enter in the format name@example.com'
        ),
      },
    },
  });

  const {
    field: {
      disabled: einIssuingCountryDisabled,
      value: einIssuingCountry,
      onChange: setEinIssuingCountry,
      onBlur: validateEinIssuingCountry,
    },
    fieldState: { error: einIssuingCountryError },
  } = useController({
    control,
    name: 'einIssuingCountry',
    disabled: entityType === EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select Number / ID Issuing Country'
        ),
      },
    },
  });

  const {
    field: {
      disabled: einDisabled,
      value: ein,
      onChange: setEin,
      onBlur: validateEin,
    },
    fieldState: { error: einError },
  } = useController({
    control,
    name: 'ein',
    disabled: entityType === EntityTypes.SoleProprietor,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Company Registration Number / ID'
        ),
        ...(einIssuingCountry === 'US'
          ? {
              isValidEin: createEinValidator(
                einValidationRule,
                'Company Registration Number / ID'
              ),
            }
          : { withValidMaxLength: createMaxLengthValidator(21) }),
      },
    },
  });

  const {
    field: {
      disabled: streetDisabled,
      value: street,
      onChange: setStreet,
      onBlur: validateStreet,
    },
    fieldState: { error: streetError },
  } = useController({
    control,
    name: 'street',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter Address / Street'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: {
      disabled: cityDisabled,
      value: city,
      onChange: setCity,
      onBlur: validateCity,
    },
    fieldState: { error: cityError },
  } = useController({
    control,
    name: 'city',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter City'),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      disabled: stateDisabled,
      value: state,
      onChange: setState,
      onBlur: validateState,
    },
    fieldState: { error: stateError },
  } = useController({
    control,
    name: 'state',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter State / Region'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });

  const {
    field: {
      disabled: postalCodeDisabled,
      value: postalCode,
      onChange: setPostalCode,
      onBlur: validatePostalCode,
    },
    fieldState: { error: postalCodeError },
  } = useController({
    control,
    name: 'postalCode',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Postal Code'),
        withValidMaxLength: createMaxLengthValidator(20),
        ...(country === 'US'
          ? {
              withValidPattern: createPatternValidator(
                regexExpressions.zip,
                'Invalid Postal Code'
              ),
            }
          : { isValidInput }),
      },
    },
  });

  const {
    field: {
      value: businessContactEmail,
      onChange: setBusinessContactEmail,
      onBlur: validateBusinessContactEmail,
    },
    fieldState: { error: businessContactEmailError },
  } = useController({
    control,
    name: 'businessContactEmail',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Business Contact Email'
        ),
        isValidMaxLength: createMaxLengthValidator(100),
        isValidEmail: createPatternValidator(
          regexExpressions.email,
          'Enter in the format name@example.com'
        ),
      },
    },
  });

  return {
    // Brand Legal Name
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameDisabled,
    companyNameError,
    // Brand DBA Name
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    // Entity Type
    entityType,
    setEntityType,
    entityTypeError,
    // Vertical Type
    vertical,
    setVertical,
    verticalDisabled,
    verticalError,
    // Stock Symbol
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolDisabled,
    stockSymbolError,
    // Stock Exchange
    stockExchange,
    setStockExchange,
    stockExchangeDisabled,
    stockExchangeError,
    // Website / Online Presence
    website,
    setWebsite,
    validateWebsite,
    websiteDisabled,
    websiteError,
    // First Name
    firstName,
    setFirstName,
    validateFirstName,
    firstNameDisabled,
    firstNameError,
    // Last Name
    lastName,
    setLastName,
    validateLastName,
    lastNameDisabled,
    lastNameError,
    // Support Phone Number
    phone,
    setPhoneCountry,
    setPhone,
    validatePhone,
    phoneError,
    // Support Email Address
    email,
    setEmail,
    validateEmail,
    emailError,
    // Country of Registration
    country,
    setCountry,
    countryError,
    // Country Registration Number / ID
    ein,
    setEin,
    validateEin,
    einDisabled,
    einError,
    // Number / ID Issuing Country
    einIssuingCountry,
    setEinIssuingCountry,
    validateEinIssuingCountry,
    einIssuingCountryDisabled,
    einIssuingCountryError,
    // Address / Street
    street,
    setStreet,
    validateStreet,
    streetDisabled,
    streetError,
    // City
    city,
    setCity,
    validateCity,
    cityDisabled,
    cityError,
    // State / Region
    state,
    setState,
    validateState,
    stateDisabled,
    stateError,
    // Postal Code
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeDisabled,
    postalCodeError,
    // Business Contact Email
    businessContactEmail,
    setBusinessContactEmail,
    validateBusinessContactEmail,
    businessContactEmailError,
  };
};

export const useTrademarkVerificationControllers = (
  control: Control<
    TrademarkVerificationFormValues,
    any,
    TrademarkVerificationFormValues
  >
) => {
  const {
    field: {
      value: registrationId,
      onChange: setRegistrationId,
      onBlur: validateRegistrationId,
    },
    fieldState: { error: registrationIdError },
  } = useController({
    control,
    name: 'registrationId',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please enter Trademark Registration Number'
        ),
        withValidPattern: createPatternValidator(
          regexExpressions.registrationId,
          'Trademark Registration Number must contain numbers only'
        ),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      value: trademarkText,
      onChange: setTrademarkText,
      onBlur: validateTrademarkText,
    },
    fieldState: { error: trademarkTextError },
  } = useController({
    control,
    name: 'trademarkText',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Trademark Wording'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  const {
    field: {
      value: trademarkOwner,
      onChange: setTrademarkOwner,
      onBlur: validateTrademarkOwner,
    },
    fieldState: { error: trademarkOwnerError },
  } = useController({
    control,
    name: 'trademarkOwner',
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(1000, true),
      },
    },
  });

  const {
    field: {
      value: patentOffice,
      onChange: setPatentOffice,
      onBlur: validatePatentOffice,
    },
    fieldState: { error: patentOfficeError },
  } = useController({
    control,
    name: 'patentOffice',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select Trademark Office'),
      },
    },
  });

  return {
    // Trademark Registration Number
    registrationId,
    setRegistrationId,
    validateRegistrationId,
    registrationIdError,
    // Trademark Wording
    trademarkText,
    setTrademarkText,
    validateTrademarkText,
    trademarkTextError,
    // Trademark Owner
    trademarkOwner,
    setTrademarkOwner,
    validateTrademarkOwner,
    trademarkOwnerError,
    // Trademark Office
    patentOffice,
    setPatentOffice,
    validatePatentOffice,
    patentOfficeError,
  };
};

export const useSendIdConfigurationFormValues = (
  control: Control<
    SenderIdConfigurationFormValues,
    any,
    SenderIdConfigurationFormValues
  >
) => {
  const {
    fields: senderIds,
    append: addSenderId,
    remove: removeSenderId,
  } = useFieldArray({
    control,
    name: 'senderIds',
  });

  return {
    senderIds,
    addSenderId,
    removeSenderId,
  };
};
